import * as React from "react";
import {
  Button,
  Popover,
  PopoverTrigger,
  PopoverSurface,
  Divider,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { DismissCircleRegular } from "@fluentui/react-icons";

// ── Shared styles used by all filter chips ───────────────────────────────────

export const useFilterChipStyles = makeStyles({
  chipButton: {
    flexShrink: "0",
    whiteSpace: "nowrap",
    borderRadius: "20px",
    paddingTop: "0.25rem",
    paddingBottom: "0.25rem",
    paddingLeft: "0.75rem",
    paddingRight: "0.75rem",
    gap: "4px",
  },
  /**
   * Inline subtitle rendered inside the chip button on the same row,
   * after the value (e.g. ``Date Range: Last 90 days  (as of 2017-04-24)``).
   * Smaller + muted than the value to read as secondary context.
   */
  chipSubtitle: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground3,
    marginLeft: "0.25rem",
  },
  labelPart: {
    fontWeight: "400",
    paddingRight: "0.25rem",
  },
  valuePart: {
    fontWeight: "600",
  },
  resetIcon: {
    display: "inline-flex",
    alignItems: "center",
    cursor: "pointer",
    marginLeft: "2px",
    fontSize: "14px",
    opacity: 0.7,
    ":hover": {
      opacity: 1,
    },
  },
  footer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "8px",
    marginTop: "12px",
  },
});

// ── Types ────────────────────────────────────────────────────────────────────

export interface FilterChipWrapperProps {
  /** Filter label (e.g. "Status") */
  label: string;
  /** Display value for the chip (e.g. "Active, Pending" or "All") */
  displayValue: string;
  /** Optional caption2-style subtitle rendered under the label/value (e.g. "as of 2027-03-25"). */
  subtitle?: string;
  /** Whether this filter has been modified from its default. Shows reset ✕ icon. */
  isModified: boolean;
  /** Called when the user clicks the ✕ reset icon on the chip */
  onReset: () => void;
  /** Whether the draft differs from applied value — controls Apply button */
  isDirty: boolean;
  /** Called when user clicks Apply */
  onApply: () => void;
  /** Called when user clicks Cancel */
  onCancel: () => void;
  /** Hide the Divider + Apply/Cancel footer (for instant-apply interactions) */
  hideFooter?: boolean;
  /** Popover surface width */
  surfaceWidth?: string;
  /** Inner content rendered inside the popover surface (above the footer) */
  children: React.ReactNode;
}

/**
 * Shared wrapper for all dashboard filter chips.
 *
 * Provides:
 * - Rounded pill chip button with `Label: Value` display
 * - ✕ reset icon when the filter is modified from default
 * - Popover container with configurable width
 * - Optional Apply / Cancel footer (hidden when `hideFooter` is true)
 *
 * Children can close the popover programmatically by calling the `onClose`
 * function passed via `FilterChipContext`.
 */
export const FilterChipWrapper: React.FC<FilterChipWrapperProps> = ({
  label,
  displayValue,
  subtitle,
  isModified,
  onReset,
  isDirty,
  onApply,
  onCancel,
  hideFooter = false,
  surfaceWidth = "260px",
  children,
}) => {
  const chipStyles = useFilterChipStyles();
  const [open, setOpen] = React.useState(false);

  const handleOpenChange = (_: unknown, data: { open: boolean }) => {
    setOpen(data.open);
  };

  const handleApply = () => {
    onApply();
    setOpen(false);
  };

  const handleCancel = () => {
    onCancel();
    setOpen(false);
  };

  const handleReset = (e: React.MouseEvent) => {
    e.stopPropagation();
    onReset();
  };

  const closePopover = React.useCallback(() => setOpen(false), []);

  return (
    <FilterChipContext.Provider value={{ close: closePopover }}>
      <Popover
        open={open}
        onOpenChange={handleOpenChange}
        positioning={{ align: "start", position: "below" }}
      >
        <PopoverTrigger disableButtonEnhancement>
          <Button appearance="secondary" size="small" className={chipStyles.chipButton}>
            <span className={chipStyles.labelPart}>{label}:</span>
            <span className={chipStyles.valuePart}>{displayValue}</span>
            {subtitle && (
              <span className={chipStyles.chipSubtitle}>{subtitle}</span>
            )}
            {isModified && (
              <span
                className={chipStyles.resetIcon}
                onClick={handleReset}
                title={`Reset ${label} filter`}
                role="button"
                tabIndex={0}
              >
                <DismissCircleRegular fontSize={14} />
              </span>
            )}
          </Button>
        </PopoverTrigger>

        <PopoverSurface style={{ width: surfaceWidth, padding: "12px" }}>
          {children}

          {!hideFooter && (
            <>
              <Divider style={{ marginTop: "8px" }} />
              <div className={chipStyles.footer}>
                <Button appearance="primary" onClick={handleApply} disabled={!isDirty}>
                  Apply
                </Button>
                <Button onClick={handleCancel}>Cancel</Button>
              </div>
            </>
          )}
        </PopoverSurface>
      </Popover>
    </FilterChipContext.Provider>
  );
};

// ── Context for children to close the popover ────────────────────────────────

interface FilterChipContextValue {
  /** Close the parent popover programmatically */
  close: () => void;
}

const FilterChipContext = React.createContext<FilterChipContextValue>({
  close: () => {},
});

/** Hook for children of FilterChipWrapper to close the popover */
export function useFilterChipClose(): () => void {
  return React.useContext(FilterChipContext).close;
}